import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatIDR } from "@/components/AppTopBar";
import "./BcaHistory.css";

export function BcaHistory({ refreshKey = 0, accounts: externalAccounts = null }) {
  const [localAccounts, setLocalAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ account_id: "", direction: "", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const hasExternalAccounts = Array.isArray(externalAccounts);
  const accounts = hasExternalAccounts ? externalAccounts : localAccounts;

  useEffect(() => {
    if (hasExternalAccounts) return;
    api.get("/accounts").then(({ data }) => setLocalAccounts(Array.isArray(data) ? data : [])).catch(() => {});
  }, [hasExternalAccounts]);
  const banks = useMemo(() => accounts.filter((a) => a.account_type === "BANK"), [accounts]);

  const load = async (wantedPage = 1, override = null) => {
    const f = override || filters;
    setBusy(true); setError("");
    try {
      const p = new URLSearchParams();
      if (f.account_id) p.set("account_id", f.account_id);
      if (f.direction) p.set("direction", f.direction);
      if (f.date_from) p.set("date_from", f.date_from);
      if (f.date_to) p.set("date_to", f.date_to);
      p.set("page", String(wantedPage)); p.set("page_size", "50");
      const { data } = await api.get(`/finance/bca-history?${p.toString()}`);
      setRows(data.rows || []); setPage(data.page || wantedPage); setTotalPages(Math.max(1, data.total_pages || 1));
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(1); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const reverseExpense = async (row) => {
    const reason = window.prompt("Alasan reversal biaya:");
    if (!reason || !reason.trim()) return;
    setBusy(true); setError("");
    try {
      await api.post(`/finance/bca-history/${row.id}/reverse-expense`, { reason: reason.trim() });
      await load(page);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Reversal gagal.");
    } finally { setBusy(false); }
  };

  const reset = () => {
    const empty = { account_id: "", direction: "", date_from: "", date_to: "" };
    setFilters(empty); setPage(1); load(1, empty);
  };

  return <section className="panel bca-history-panel">
    <div className="panel-head">
      <div>
        <h3 className="dash-h" style={{ margin: 0 }}>History BCA IN / OUT</h3>
        <div className="hint">Jejak uang rekening BANK dari ledger canonical. Tanggal Debit adalah tanggal mutasi rekening khusus biaya operasional; Tgl Dicatat ERP tetap timestamp asli.</div>
      </div>
    </div>
    {error && <div className="alert-error">{error}</div>}
    <div className="bca-history-filters">
      <div className="bca-history-filter-field">
        <label className="field-label">Bank</label>
        <select className="field-input" value={filters.account_id} onChange={(e) => setFilters({...filters, account_id:e.target.value})}>
          <option value="">Semua BANK</option>
          {banks.map((b) => <option key={b.account_id} value={b.account_id}>{b.account_name}</option>)}
        </select>
      </div>
      <div className="bca-history-filter-field">
        <label className="field-label">Arah</label>
        <select className="field-input" value={filters.direction} onChange={(e) => setFilters({...filters, direction:e.target.value})}>
          <option value="">IN + OUT</option>
          <option value="IN">IN</option>
          <option value="OUT">OUT</option>
        </select>
      </div>
      <div className="bca-history-filter-field">
        <label className="field-label">Dari Tgl ERP</label>
        <input className="field-input" type="date" value={filters.date_from} onChange={(e) => setFilters({...filters, date_from:e.target.value})} />
      </div>
      <div className="bca-history-filter-field">
        <label className="field-label">Sampai Tgl ERP</label>
        <input className="field-input" type="date" value={filters.date_to} onChange={(e) => setFilters({...filters, date_to:e.target.value})} />
      </div>
      <div className="bca-history-filter-actions">
        <button className="btn-primary" disabled={busy} onClick={() => load(1)}>Terapkan</button>
        <button className="btn-ghost" disabled={busy} onClick={reset}>Reset</button>
      </div>
    </div>
    <div className="bca-history-table-wrap">
      <table className="data-table bca-history-table">
        <colgroup>
          {["11.5%","10.5%","6%","6.5%","13%","10.5%","8%","8%","10%","9%","7%"].map((width, i) => <col key={i} style={{ width }} />)}
        </colgroup>
        <thead><tr>
          <th>Tgl Dicatat ERP</th><th>Tgl Debit Bank</th><th>Bank</th><th>IN/OUT</th>
          <th>Jenis</th><th>Referensi</th>
          <th className="bca-history-align-right">MODAL</th>
          <th className="bca-history-align-right">PROFIT</th>
          <th className="bca-history-align-right">UNCLASSIFIED</th>
          <th className="bca-history-align-right">Nominal</th>
          <th className="bca-history-align-center">Aksi</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={11} className="empty-cell">{busy ? "Memuat..." : "Belum ada movement."}</td></tr>}
          {rows.map((r) => <tr key={r.id}>
            <td data-label="Tgl Dicatat ERP"><span className="bca-history-date">{r.created_at ? <>
              <span>{new Date(r.created_at).toLocaleDateString("id-ID")}</span>
              <span>{new Date(r.created_at).toLocaleTimeString("id-ID", {hour12:false})}</span>
            </> : "-"}</span></td>
            <td data-label="Tgl Debit Bank" data-testid="bca-debit-date">{r.bank_debit_date || "-"}</td>
            <td data-label="Bank">{r.account_name}</td>
            <td data-label="IN/OUT"><span className={`status-pill ${r.direction === "IN" ? "on" : "off"}`}>{r.direction}</span></td>
            <td data-label="Jenis">{r.movement_type}</td>
            <td data-label="Referensi">{r.reference || "-"}</td>
            <td data-label="MODAL" className="bca-history-money">{formatIDR(r.modal)}</td>
            <td data-label="PROFIT" className="bca-history-money">{formatIDR(r.profit)}</td>
            <td data-label="UNCLASSIFIED" className="bca-history-money">{formatIDR(r.unclassified)}</td>
            <td data-label="Nominal" className="bca-history-money bca-history-money--total">{formatIDR(r.amount)}</td>
            <td data-label="Aksi" className="bca-history-align-center">{r.reversal_available ? <button className="btn-mini" disabled={busy} onClick={() => reverseExpense(r)}>Reversal</button> : "-"}</td>
          </tr>)}
        </tbody>
      </table>
    </div>
    {totalPages > 1 && <div className="form-actions" style={{justifyContent:"space-between"}}>
      <button className="btn-ghost" disabled={busy || page <= 1} onClick={() => load(page - 1)}>← Sebelumnya</button>
      <span className="hint">Halaman {page} / {totalPages}</span>
      <button className="btn-ghost" disabled={busy || page >= totalPages} onClick={() => load(page + 1)}>Berikutnya →</button>
    </div>}
  </section>;
}
